import {
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { SupabaseService } from '../../services/supabase.service';
import { UserService } from '../../services/user.service';
import { TranslatePipe } from '../../../pipes/translate.pipe';

/** Payload rendered in the card once resolved. */
interface NextMatchInfo {
  tournamentId: string;
  tournamentName: string;
  matchNumber: number;
  yourTeamName: string;
  opponentName: string;
  scheduledAt: string; // ISO
  court: string | null;
}

/**
 * Minimal shape of a `tournaments.tournament_registrations` row we care
 * about. `registration_form` is JSONB; the teammate entries live inside
 * any array value shaped like `{ id: string, name: string }`.
 */
interface RegistrationRow {
  id: number;
  tournament_id: string;
  joined_by_user_id: string | null;
  joined_by_fullname: string | null;
  registration_form: Record<string, unknown> | null;
}

/** Minimal shape of a `tournaments.tournament_matches` row. */
interface TournamentMatchRow {
  id: number;
  tournament_id: string;
  match_number: number;
  team_1: number | null;
  team_2: number | null;
  scheduled_at: string | null;
  scheduled_end_at: string | null;
  court: string | null;
  status: string;
}

@Component({
  selector: 'app-my-next-match',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './my-next-match.component.html',
  styleUrl: './my-next-match.component.scss',
})
export class MyNextMatchComponent implements OnInit, OnDestroy {
  private readonly supabaseService = inject(SupabaseService);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  /** Loading state while we query Supabase. */
  readonly loading = signal<boolean>(true);

  /** The resolved next match, or null when the user has nothing upcoming. */
  readonly nextMatch = signal<NextMatchInfo | null>(null);

  /**
   * Ticks once per minute so the countdown recomputes automatically
   * without triggering any template pipes. Incremented by `setInterval`
   * scheduled in `ngOnInit` and torn down in `ngOnDestroy`.
   */
  private readonly tick = signal<number>(0);
  private countdownIntervalId: ReturnType<typeof setInterval> | null = null;

  /**
   * Human-readable countdown derived from `nextMatch().scheduledAt` and
   * the minute tick. Returns an empty string when there's no match.
   *
   * Buckets:
   *   < 1 minute        → "Starting now"
   *   < 60 minutes      → "In X minutes"
   *   < 24 hours        → "In X hours, Y minutes"
   *   next calendar day → "Tomorrow at HH:MM"
   *   later             → "Thu, Apr 17 at HH:MM"
   */
  readonly countdown = computed<string>(() => {
    // Read `tick()` to establish dependency for reactive updates.
    this.tick();

    const match = this.nextMatch();
    if (!match) return '';

    const scheduled = new Date(match.scheduledAt);
    if (Number.isNaN(scheduled.getTime())) return '';

    const now = new Date();
    const diffMs = scheduled.getTime() - now.getTime();
    const diffMinutes = Math.round(diffMs / 60000);

    if (diffMinutes < 1) {
      return 'Starting now';
    }
    if (diffMinutes < 60) {
      return `In ${diffMinutes} minutes`;
    }
    if (diffMinutes < 60 * 24) {
      const hours = Math.floor(diffMinutes / 60);
      const minutes = diffMinutes % 60;
      if (minutes === 0) {
        return `In ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
      }
      return `In ${hours} ${hours === 1 ? 'hour' : 'hours'}, ${minutes} minutes`;
    }

    const timePart = this.formatAthensTime(scheduled);
    if (this.isNextCalendarDay(now, scheduled)) {
      return `Tomorrow at ${timePart}`;
    }
    return `${this.formatAthensShortDate(scheduled)} at ${timePart}`;
  });

  ngOnInit(): void {
    this.loadMyNextMatch();

    // Recompute the countdown every 60s. Store the handle so ngOnDestroy
    // can clear it — the `DestroyRef` is available for subscription-based
    // teardown but setInterval doesn't flow through it.
    this.countdownIntervalId = setInterval(() => {
      this.tick.update((t) => t + 1);
    }, 60_000);
  }

  ngOnDestroy(): void {
    if (this.countdownIntervalId !== null) {
      clearInterval(this.countdownIntervalId);
      this.countdownIntervalId = null;
    }
  }

  /**
   * Main fetch:
   *  1. Resolve user + club
   *  2. Find brackets currently in_progress for this club
   *  3. For each tournament, pull ready/in_progress matches ordered by time
   *  4. Load registrations for those tournaments, pick the soonest match
   *     where the current user is involved (captain or teammate), and
   *     build the NextMatchInfo.
   */
  async loadMyNextMatch(): Promise<void> {
    this.loading.set(true);
    try {
      const userId = this.userService.currentUser?.id;
      const clubId = this.supabaseService.clubId;
      if (!userId || !clubId) {
        this.nextMatch.set(null);
        return;
      }

      // Access the `tournaments` schema directly (now in generated types).
      const tournamentsSchema =
        this.supabaseService.supabase.schema('tournaments');

      // Step 1: find tournaments in this club — we need their names later.
      const tournamentsRes = await tournamentsSchema
        .from('tournaments')
        .select('id, name')
        .eq('club_id', clubId);

      if (tournamentsRes.error) {
        console.error(
          'MyNextMatch: error fetching club tournaments:',
          tournamentsRes.error
        );
        this.nextMatch.set(null);
        return;
      }
      const tournaments =
        (tournamentsRes.data as { id: string; name: string | null }[] | null) ??
        [];
      if (tournaments.length === 0) {
        this.nextMatch.set(null);
        return;
      }

      const tournamentIds = tournaments.map((t) => t.id);
      const nameById = new Map<string, string>();
      for (const t of tournaments) {
        nameById.set(t.id, t.name ?? 'Tournament');
      }

      // Step 2: narrow to tournaments whose bracket is in_progress. Two
      // queries is fine here — the join through PostgREST is awkward for
      // tables without FKs in the generated types.
      const bracketsRes = await tournamentsSchema
        .from('tournament_brackets')
        .select('tournament_id')
        .in('tournament_id', tournamentIds)
        .eq('status', 'in_progress');

      if (bracketsRes.error) {
        console.error(
          'MyNextMatch: error fetching in_progress brackets:',
          bracketsRes.error
        );
        this.nextMatch.set(null);
        return;
      }
      const inProgressTournamentIds = (
        (bracketsRes.data as { tournament_id: string }[] | null) ?? []
      ).map((row) => row.tournament_id);
      if (inProgressTournamentIds.length === 0) {
        this.nextMatch.set(null);
        return;
      }

      // Step 3: load candidate matches across all active tournaments in one
      // shot, ordered by scheduled_at so the first user-involved match we
      // find is the earliest.
      const matchesRes = await tournamentsSchema
        .from('tournament_matches')
        .select(
          'id, tournament_id, match_number, team_1, team_2, scheduled_at, scheduled_end_at, court, status'
        )
        .in('tournament_id', inProgressTournamentIds)
        .in('status', ['ready', 'in_progress'])
        .order('scheduled_at', { ascending: true, nullsFirst: false });

      if (matchesRes.error) {
        console.error(
          'MyNextMatch: error fetching tournament matches:',
          matchesRes.error
        );
        this.nextMatch.set(null);
        return;
      }
      const candidateMatches =
        (matchesRes.data as TournamentMatchRow[] | null) ?? [];

      // Drop matches without a scheduled time — we can't rank them and
      // the task explicitly asks to skip them.
      const scheduledMatches = candidateMatches
        .filter((m) => !!m.scheduled_at)
        .sort(
          (a, b) =>
            new Date(a.scheduled_at as string).getTime() -
            new Date(b.scheduled_at as string).getTime()
        );
      if (scheduledMatches.length === 0) {
        this.nextMatch.set(null);
        return;
      }

      // Step 4: load the registrations for these tournaments so we can
      // match team_1/team_2 IDs to captain + teammates.
      const registrationIds = new Set<number>();
      for (const m of scheduledMatches) {
        if (m.team_1 !== null) registrationIds.add(m.team_1);
        if (m.team_2 !== null) registrationIds.add(m.team_2);
      }
      if (registrationIds.size === 0) {
        this.nextMatch.set(null);
        return;
      }

      const registrationsRes = await tournamentsSchema
        .from('tournament_registrations')
        .select(
          'id, tournament_id, joined_by_user_id, registration_form'
        )
        .in('id', Array.from(registrationIds));

      if (registrationsRes.error) {
        console.error(
          'MyNextMatch: error fetching tournament registrations:',
          registrationsRes.error
        );
        this.nextMatch.set(null);
        return;
      }

      // joined_by_fullname isn't a column on the table — enrich by
      // pulling the user rows separately.
      const captainIds = [
        ...new Set(
          (registrationsRes.data ?? [])
            .map((r) => r.joined_by_user_id)
            .filter((id): id is string => typeof id === 'string')
        ),
      ];
      const captainNameById = new Map<string, string>();
      if (captainIds.length > 0) {
        const usersRes = await this.supabaseService.supabase
          .from('users')
          .select('user_id, fullname')
          .in('user_id', captainIds);
        for (const u of usersRes.data ?? []) {
          if (u.user_id && u.fullname) captainNameById.set(u.user_id, u.fullname);
        }
      }

      const registrations: RegistrationRow[] = (registrationsRes.data ?? []).map(
        (r) => ({
          id: r.id,
          tournament_id: r.tournament_id ?? '',
          joined_by_user_id: r.joined_by_user_id,
          joined_by_fullname: r.joined_by_user_id
            ? captainNameById.get(r.joined_by_user_id) ?? null
            : null,
          registration_form:
            (r.registration_form as Record<string, unknown> | null) ?? null,
        })
      );

      const registrationById = new Map<number, RegistrationRow>();
      for (const reg of registrations) {
        registrationById.set(reg.id, reg);
      }

      // Walk matches in scheduled order and pick the first one where the
      // user is a participant in either team (captain OR teammate).
      for (const match of scheduledMatches) {
        const team1Reg =
          match.team_1 !== null ? registrationById.get(match.team_1) : null;
        const team2Reg =
          match.team_2 !== null ? registrationById.get(match.team_2) : null;
        if (!team1Reg && !team2Reg) continue;

        const userInTeam1 = !!team1Reg && this.isUserInRegistration(team1Reg, userId);
        const userInTeam2 = !!team2Reg && this.isUserInRegistration(team2Reg, userId);
        if (!userInTeam1 && !userInTeam2) continue;

        const yourReg = userInTeam1 ? team1Reg : team2Reg;
        const opponentReg = userInTeam1 ? team2Reg : team1Reg;
        if (!yourReg) continue;

        const tournamentName =
          nameById.get(match.tournament_id) ?? 'Tournament';
        const yourTeamName = this.buildTeamName(yourReg);
        const opponentName = opponentReg
          ? this.buildTeamName(opponentReg)
          : 'TBD';

        this.nextMatch.set({
          tournamentId: match.tournament_id,
          tournamentName,
          matchNumber: match.match_number,
          yourTeamName,
          opponentName,
          scheduledAt: match.scheduled_at as string,
          court: match.court,
        });
        return;
      }

      // No match involves the current user.
      this.nextMatch.set(null);
    } catch (err) {
      console.error('MyNextMatch: unexpected error loading next match:', err);
      this.nextMatch.set(null);
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Navigate to the tournament bracket when the card is clicked.
   *
   * We pass two query params that the bracket view honors:
   *   focus=<matchNumber>  — the specific match to scroll to / highlight
   *   action=score         — if present AND the match is submittable, the
   *                          bracket view auto-opens the score entry dialog
   *                          so the athlete doesn't have to hunt for their
   *                          match card and click the "Enter score" button
   *                          manually.
   *
   * The bracket view scrubs these params from the URL after handling them
   * so refreshing the page doesn't reopen the dialog.
   */
  onCardClick(): void {
    const match = this.nextMatch();
    if (!match) return;
    this.router.navigate(['/tournaments/bracket', match.tournamentId], {
      queryParams: {
        focus: match.matchNumber,
        action: 'score',
      },
    });
  }

  /**
   * Is `userId` the captain or a teammate on this registration?
   * Scans every array value inside `registration_form` looking for
   * objects with an `id` property matching the user — the same shape
   * `bracket.service.ts::getUserIdsForRegistration` relies on.
   */
  private isUserInRegistration(
    reg: RegistrationRow,
    userId: string
  ): boolean {
    if (reg.joined_by_user_id === userId) return true;

    const form = reg.registration_form;
    if (!form || typeof form !== 'object') return false;

    for (const value of Object.values(form)) {
      if (!Array.isArray(value)) continue;
      for (const item of value) {
        if (item && typeof item === 'object' && 'id' in item) {
          const candidate = (item as { id: unknown }).id;
          if (typeof candidate === 'string' && candidate === userId) {
            return true;
          }
        }
      }
    }
    return false;
  }

  /**
   * Build a human-readable team label: captain name plus any teammate
   * names extracted from `registration_form` arrays of `{id, name}`.
   * Mirrors `bracket.service.ts::buildTeamDisplayName` so both surfaces
   * render the same team strings.
   */
  private buildTeamName(reg: RegistrationRow): string {
    const parts: string[] = [];
    if (reg.joined_by_fullname) {
      parts.push(reg.joined_by_fullname);
    }

    const form = reg.registration_form;
    if (form && typeof form === 'object') {
      for (const value of Object.values(form)) {
        if (!Array.isArray(value)) continue;
        for (const item of value) {
          if (
            item &&
            typeof item === 'object' &&
            'name' in item &&
            typeof (item as { name: unknown }).name === 'string'
          ) {
            const name = (item as { name: string }).name.trim();
            if (name && !parts.includes(name)) {
              parts.push(name);
            }
          }
        }
      }
    }

    if (parts.length === 0) {
      return `Team ${reg.id}`;
    }
    return parts.join(' + ');
  }

  /** Format the time as HH:MM in Europe/Athens. */
  private formatAthensTime(date: Date): string {
    try {
      return new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Athens',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(date);
    } catch {
      return '';
    }
  }

  /** Short date label like "Thu, Apr 17" in Europe/Athens. */
  private formatAthensShortDate(date: Date): string {
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: 'Europe/Athens',
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      }).format(date);
    } catch {
      return '';
    }
  }

  /**
   * True when `target` falls on the calendar day after `reference` in the
   * Europe/Athens timezone. Comparing via the localized short date string
   * avoids UTC/local offset edge cases around midnight.
   */
  private isNextCalendarDay(reference: Date, target: Date): boolean {
    const tomorrow = new Date(reference.getTime() + 24 * 60 * 60 * 1000);
    return (
      this.athensDayKey(target) === this.athensDayKey(tomorrow)
    );
  }

  private athensDayKey(date: Date): string {
    try {
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Athens',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(date);
    } catch {
      return date.toISOString().slice(0, 10);
    }
  }
}
